/* eslint-disable @typescript-eslint/no-explicit-any */
import { EvidencePart, FieldType, ProgramSkill } from "@interfaces/Interfaces";
import { PreviousIncidentDetails, previousIncidentDetailsAtom } from "@state/EvidenceState";
import { useRecoilStateLoadable } from "recoil";

const PREVIOUS_DETAILS_KEYS = {
  EVENT: "event-type",
  DATE: "date",
  INCIDENT_NUMBER: "incident-number",
  PATIENT_AGE: "patient-age",
  PATIENT_SEX: "patient-sex",
};

const INCIDENT = "INCIDENT";

function valueIsIncident(value: string) {
  return value.toUpperCase().trim() === INCIDENT;
}

function partIsEventType(part: EvidencePart) {
  return part.PreviousDetailsKey === PREVIOUS_DETAILS_KEYS.EVENT;
}

type UpdateReturnValue = "SKIP" | "PRESENT_PREVIOUS_INCIDENT";

export const usePreviousIncidentDetails = () => {
  const [previousIncidentDetails, setPreviousIncidentDetails] = useRecoilStateLoadable<PreviousIncidentDetails | null>(
    previousIncidentDetailsAtom
  );

  const addPreviousIncidentDetailsToEvidence = (params: {
    evidenceParts: EvidencePart[];
    evidenceJSON: any;
    programSkills: ProgramSkill[];
  }) => {
    const { evidenceParts, evidenceJSON, programSkills } = params;

    const copy = structuredClone(evidenceJSON);
    const evidenceToAdd: Record<string, any> = {};

    const activityKey = Object.keys(copy).find(
      (key) => key.toUpperCase().endsWith("TYPE") && valueIsIncident(copy[key])
    );

    if (!previousIncidentDetails.contents) {
      return copy;
    }

    if (activityKey) {
      const activityKeyStart = activityKey.substring(0, activityKey.length - 4);
      const partsToAdd = evidenceParts.filter(
        (evidencePart) => evidencePart.Name.startsWith(activityKeyStart) && evidencePart.PreviousDetailsKey
      );

      partsToAdd.forEach((evidencePart) => {
        const previousIncidentDetailsObject = !partIsEventType(evidencePart)
          ? previousIncidentDetails.contents[evidencePart.PreviousDetailsKey as keyof PreviousIncidentDetails]
          : undefined;

        if (previousIncidentDetailsObject) {
          const previousValue = previousIncidentDetailsObject.value;

          // If evidence part has a list of values, check if the previous value is in the list before adding it to the evidence to add object
          // E.g. patient sex EvidencePart sometimes has Prefer not to say, which won't be added if the value is not in the list
          if (evidencePart["List of values"]) {
            const listValues = evidencePart["List of values"].split("\n");

            if (listValues.includes(previousValue)) {
              evidenceToAdd[evidencePart.Name as string] = previousValue;
            }
          } else {
            evidenceToAdd[evidencePart.Name as string] = previousValue;
          }
        }
      });

      if (copy["Date of event"]) {
        const previousIncidentDetailsObject = previousIncidentDetails.contents["date" as keyof PreviousIncidentDetails];

        if (previousIncidentDetailsObject) {
          const previousValue = previousIncidentDetailsObject.value;
          evidenceToAdd["Date of event"] = previousValue;
        }
      }
    } else {
      const skillUsedKey = evidenceParts.find((part) => part["Field type"] === FieldType.SkillsSelect)?.Name;
      const skillUsed = skillUsedKey ? copy[skillUsedKey] : undefined;

      if (skillUsed) {
        const skill: ProgramSkill | undefined = programSkills.find((skill) => skill.Name === skillUsed);

        if (skill?.TriggerPreviousIncidentDetails) {
          const skillKey = skill.Name.replace(/\s/g, "").toUpperCase();
          const partsToAdd = evidenceParts.filter(
            (evidencePart) => evidencePart.Name.toUpperCase().startsWith(skillKey) && evidencePart.PreviousDetailsKey
          );

          partsToAdd.forEach((evidencePart) => {
            const previousIncidentDetailsObject = !partIsEventType(evidencePart)
              ? previousIncidentDetails.contents[evidencePart.PreviousDetailsKey as keyof PreviousIncidentDetails]
              : undefined;

            if (previousIncidentDetailsObject) {
              const previousValue = previousIncidentDetailsObject.value;

              // If evidence part has a list of values, check if the previous value is in the list before adding it to the evidence to add object
              // E.g. patient sex EvidencePart sometimes has Prefer not to say, which won't be added if the value is not in the list
              if (evidencePart["List of values"]) {
                const listValues = evidencePart["List of values"].split("\n");

                if (listValues.includes(previousValue)) {
                  evidenceToAdd[evidencePart.Name as string] = previousValue;
                }
              } else {
                evidenceToAdd[evidencePart.Name as string] = previousValue;
              }
            }
          });

          if (copy["Date of event"]) {
            const previousIncidentDetailsObject =
              previousIncidentDetails.contents["date" as keyof PreviousIncidentDetails];

            if (previousIncidentDetailsObject) {
              const previousValue = previousIncidentDetailsObject.value;
              evidenceToAdd["Date of event"] = previousValue;
            }
          }
        }
      }
    }

    const updatedEvidenceJSON = { ...copy, ...evidenceToAdd };

    return updatedEvidenceJSON;
  };

  const createPreviousIncidentDetailsObject = (params: { evidenceParts: EvidencePart[]; evidenceJSON: any }) => {
    const { evidenceParts, evidenceJSON } = params;

    const copy = structuredClone(evidenceJSON);
    delete copy.programInfo;

    // Only proceed if the event type is an incident
    const eventTypeKey = Object.keys(copy).find(
      (key) => key.toUpperCase().endsWith("TYPE") && valueIsIncident(copy[key])
    );

    if (!eventTypeKey) {
      return;
    }

    const partsToCheck: EvidencePart[] = [];

    for (const key in copy) {
      const evidencePart = evidenceParts.find((part) => part.Name === key);

      if (evidencePart && evidencePart.PreviousDetailsKey) {
        if (!partIsEventType(evidencePart)) {
          partsToCheck.push(evidencePart);
        }
      }
    }

    const object: Record<string, { key: string; value: any }> = {};

    for (const part of partsToCheck) {
      const value = copy[part.Name];

      if (value) {
        object[part.PreviousDetailsKey as keyof PreviousIncidentDetails] = {
          key: part.PreviousDetailsKey as string,
          value: value as never,
        };
      }
    }

    if (Object.keys(object).length > 0) {
      setPreviousIncidentDetails(object as PreviousIncidentDetails);
    } else {
      setPreviousIncidentDetails(null);
    }
  };

  const updateIncidentDetails = (params: {
    evidencePart: EvidencePart;
    value: any;
    isInput?: boolean;
  }): UpdateReturnValue => {
    const { evidencePart, value } = params;

    if (evidencePart.PreviousDetailsKey === PREVIOUS_DETAILS_KEYS.EVENT) {
      if (valueIsIncident(value)) {
        if (previousIncidentDetails.contents) {
          return "PRESENT_PREVIOUS_INCIDENT";
        }
      }
    }

    return "SKIP";
  };

  const shouldShowReuseIncidentModal = () => {
    return previousIncidentDetails.contents !== null;
  };

  return {
    updateIncidentDetails,
    addPreviousIncidentDetailsToEvidence,
    createPreviousIncidentDetailsObject,
    shouldShowReuseIncidentModal,
  };
};
